import { Scene } from "@babylonjs/core/scene";
import { Engine } from "@babylonjs/core/Engines/engine";
import { Vector3, Quaternion } from "@babylonjs/core/Maths/math.vector";
import { Vector2 } from "@babylonjs/core/Maths/math.vector";
import { Color4 } from "@babylonjs/core/Maths/math.color";
import { FreeCamera } from "@babylonjs/core/Cameras/freeCamera";
import { Camera } from "@babylonjs/core/Cameras/camera";
import { RenderTargetTexture } from "@babylonjs/core/Materials/Textures/renderTargetTexture";
import { BlurPostProcess } from "@babylonjs/core/PostProcesses/blurPostProcess";
import { ShaderMaterial } from "@babylonjs/core/Materials/shaderMaterial";
import { Effect } from "@babylonjs/core/Materials/effect";
import { CreateGround } from "@babylonjs/core/Meshes/Builders/groundBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { COLLIDER_MESH_SUFFIX, SELECTION_MESH_SUFFIX } from "../behaviors/boxcollider";

/** Internal mesh name prefix — all shadow infrastructure uses this. */
const PREFIX = '__shadow';

/** Fixed ground plane Y — sits just above y=0 to overlay floor geometry. */
const GROUND_Y = 0.002;

/** Fixed shadow area half-extent (metres). */
const SHADOW_HALF = 10;

/** Fixed camera height above ground. */
const CAM_HEIGHT = 50;

/**
 * Contact shadow system that renders a soft shadow on a fixed ground plane.
 *
 * Works by capturing the scene from a top-down orthographic camera into a
 * RenderTargetTexture, blurring it, and displaying the result on a ground
 * plane via a custom shader. Meshes render with their own materials so
 * InstancedMesh works without material overrides.
 *
 * The shadow camera and ground plane use fixed positions — no dynamic
 * scene-extent computation. The camera clips at the ground plane so
 * geometry below y=0 does not cast shadows.
 *
 * The owner moves the shadow plane with the scene by calling
 * `syncToTransform` (desktop) or `setPosition` (XR, where reference space
 * offsets position the scene). The shadow camera and ground live under an
 * independent anchor node (`__shadowAnchor`) so they survive
 * SceneObject.reload(), which disposes non-SceneObject children of the scene
 * root.
 */
export class ContactShadow {
    private _scene: Scene;
    private _engine: Engine;
    private _anchor: TransformNode;
    private _camera: FreeCamera;
    private _rtt: RenderTargetTexture;
    private _ground: Mesh;
    private _groundHeight: number;
    private _blurPasses: BlurPostProcess[] = [];

    constructor(scene: Scene, engine: Engine, groundHeight?: number) {
        this._scene = scene;
        this._engine = engine;

        const gy = groundHeight ?? GROUND_Y;
        this._groundHeight = gy;

        ContactShadow._registerShaders();

        // Independent anchor node — NOT a child of the scene root, so it
        // survives SceneObject.reload() which disposes non-SceneObject children.
        // The anchor's world matrix is synced to the scene root each frame.
        const anchor = new TransformNode(`${PREFIX}Anchor`, scene);
        anchor.rotationQuaternion = Quaternion.Identity();
        this._anchor = anchor;

        // Orthographic camera looking straight down — fixed size, clips at ground
        const cam = new FreeCamera(`${PREFIX}Camera`, new Vector3(0, CAM_HEIGHT, 0), scene);
        cam.mode = Camera.ORTHOGRAPHIC_CAMERA;
        cam.orthoLeft = -SHADOW_HALF;
        cam.orthoRight = SHADOW_HALF;
        cam.orthoTop = SHADOW_HALF;
        cam.orthoBottom = -SHADOW_HALF;
        cam.minZ = 0.1;
        cam.maxZ = CAM_HEIGHT - gy;
        // Up must not be parallel to the view. With the default +Y, the image's
        // up axis comes from a 1e-16 rounding residue of Math.sin(PI / 4) in
        // Babylon's per-frame view rebuild: V8 gives -Z, Firefox on Windows
        // (Microsoft's CRT sin) gives +Z, and every shadow landed mirrored
        // through the origin. -Z is the ground shader's +v (vShadowUV below).
        cam.upVector = new Vector3(0, 0, -1);
        cam.setTarget(new Vector3(0, gy, 0));
        cam.parent = anchor;
        this._camera = cam;

        // RTT — meshes render with their own materials; opaque pixels → alpha=1
        const rtt = new RenderTargetTexture(`${PREFIX}RTT`, 512, scene, false, true);
        rtt.activeCamera = cam;
        rtt.clearColor = new Color4(1, 1, 1, 0);
        rtt.renderParticles = false;
        rtt.refreshRate = RenderTargetTexture.REFRESHRATE_RENDER_ONEVERYFRAME;
        rtt.renderList = [];
        // Babylon caches renderList's length before onBeforeRender during
        // readiness checks. Shrinking that same array there makes it read
        // undefined meshes when a graph starts after a scene replacement.
        // The custom-list hook supplies both the current list and its length
        // for readiness AND rendering, including newly added/hidden meshes.
        rtt.getCustomRenderList = () => this._getRenderList();
        this._rtt = rtt;
        scene.customRenderTargets.push(rtt);

        // Two-pass Gaussian blur for soft edges
        const blur = (name: string, dir: Vector2) =>
            new BlurPostProcess(`${PREFIX}${name}`, dir, 15, 1.0, null, undefined, engine);
        const passes = [
            blur('BlurH1', new Vector2(1, 0)),
            blur('BlurV1', new Vector2(0, 1)),
            blur('BlurH2', new Vector2(1, 0)),
            blur('BlurV2', new Vector2(0, 1)),
        ];
        for (const p of passes) rtt.addPostProcess(p);
        this._blurPasses = passes;

        // Ground plane displaying the blurred shadow
        const ground = CreateGround(`${PREFIX}Ground`, { width: 100, height: 100 }, scene);
        ground.position.y = gy;
        ground.isPickable = false;
        ground.parent = anchor;

        const mat = new ShaderMaterial(`${PREFIX}GroundMat`, scene, {
            vertex: '__contactShadowGround',
            fragment: '__contactShadowGround',
        }, {
            attributes: ['position', 'uv'],
            uniforms: ['worldViewProjection', 'shadowCenter', 'shadowSize', 'shadowIntensity'],
            samplers: ['shadowTex'],
            needAlphaBlending: true,
        });
        mat.setTexture('shadowTex', rtt);
        mat.setFloat('shadowIntensity', 0.5);
        mat.setFloat('shadowSize', SHADOW_HALF * 2);
        mat.setVector2('shadowCenter', Vector2.Zero());
        mat.backFaceCulling = false;
        mat.alphaMode = 2; // ALPHA_COMBINE
        ground.material = mat;
        this._ground = ground;
    }

    /**
     * Update the ground plane height at runtime (scene-local Y).
     * Repositions the ground mesh and adjusts the shadow camera clip distance.
     */
    setGroundHeight(y: number): void {
        this._groundHeight = y;
        this._ground.position.y = y;
        this._camera.maxZ = CAM_HEIGHT - y;
        this._camera.setTarget(new Vector3(0, y, 0));
    }

    dispose(): void {
        const scene = this._scene;
        const idx = scene.customRenderTargets.indexOf(this._rtt);
        if (idx !== -1) scene.customRenderTargets.splice(idx, 1);
        this._rtt.dispose();
        for (const p of this._blurPasses) p.dispose();
        this._camera.dispose();
        this._ground.dispose();
        this._anchor.dispose();
    }

    // ---------------------------------------------------------------
    // Anchor placement
    // ---------------------------------------------------------------

    /**
     * Copy the given transform's world position and scale to the shadow anchor.
     *
     * Rotation is intentionally NOT copied — the shadow ground plane stays
     * world-axis-aligned so shadows always cast onto a horizontal floor. The
     * source's rotation can be image-anchor-driven (180° Y, 180° Y + 90° X)
     * which would otherwise tilt the shadow plane into a wall.
     *
     * Called from TraceScene's onAfterWorldMatrixUpdateObservable handler on
     * the scene root (desktop path). Do NOT call computeWorldMatrix here —
     * the observable fires after the matrix is already updated, and
     * calling it again would re-trigger the observable → infinite recursion.
     */
    syncToTransform(source: TransformNode): void {
        // Decompose into a scratch quaternion that's then discarded, so the
        // anchor's own rotationQuaternion stays at identity.
        const scratchRot = new Quaternion();
        source.getWorldMatrix().decompose(
            this._anchor.scaling,
            scratchRot,
            this._anchor.position,
        );
    }

    /**
     * Set the shadow anchor position directly (for XR where the scene root
     * stays at identity and positioning is via reference space offsets).
     * The anchor rotation stays at identity — shadows are always
     * gravity-aligned regardless of marker orientation.
     */
    setPosition(position: Vector3): void {
        this._anchor.position.copyFrom(position);
    }

    // ---------------------------------------------------------------
    // Private
    // ---------------------------------------------------------------

    /**
     * Current shadow casters, shared by RTT readiness and rendering.
     */
    private _getRenderList(): AbstractMesh[] {
        return this._scene.meshes.filter(mesh =>
            mesh.isVisible && mesh.isEnabled() && !ContactShadow._shouldSkip(mesh));
    }

    // ---------------------------------------------------------------
    // Static helpers
    // ---------------------------------------------------------------

    private static _shadersRegistered = false;

    private static _registerShaders(): void {
        if (ContactShadow._shadersRegistered) return;
        ContactShadow._shadersRegistered = true;

        Effect.ShadersStore['__contactShadowGroundVertexShader'] = /* glsl */`
            precision highp float;
            attribute vec3 position;
            attribute vec2 uv;
            uniform mat4 worldViewProjection;
            uniform vec2 shadowCenter;
            uniform float shadowSize;
            varying vec2 vShadowUV;
            void main() {
                gl_Position = worldViewProjection * vec4(position, 1.0);
                float halfSize = shadowSize * 0.5;
                vShadowUV.x = (position.x - (shadowCenter.x - halfSize)) / shadowSize;
                vShadowUV.y = 1.0 - (position.z - (shadowCenter.y - halfSize)) / shadowSize;
            }
        `;

        Effect.ShadersStore['__contactShadowGroundFragmentShader'] = /* glsl */`
            precision highp float;
            uniform sampler2D shadowTex;
            uniform float shadowIntensity;
            varying vec2 vShadowUV;
            void main() {
                if (vShadowUV.x < 0.0 || vShadowUV.x > 1.0 || vShadowUV.y < 0.0 || vShadowUV.y > 1.0) {
                    discard;
                }
                float shadow = texture2D(shadowTex, vShadowUV).a;
                gl_FragColor = vec4(0.0, 0.0, 0.0, shadow * shadowIntensity);
            }
        `;
    }

    /** Returns true for meshes that should be excluded from shadow casting. */
    private static _shouldSkip(mesh: AbstractMesh): boolean {
        const name = mesh.name;
        if (name.startsWith('__')) return true;
        if (name.endsWith(COLLIDER_MESH_SUFFIX)) return true;
        if (name.endsWith(SELECTION_MESH_SUFFIX)) return true;
        if (name === 'BackgroundPlane' || name === 'BackgroundSkybox' || name === 'hdrSkyBox') return true;
        // Renderer behavior sets metadata.castShadows = false
        if ((mesh.metadata as { castShadows?: boolean } | null)?.castShadows === false) return true;
        return false;
    }
}
